import { useState, useRef, useCallback, useEffect } from "react";
import { loadGoogleMapsScript, markGoogleMapsFailed } from "@/lib/googleMapsLoader";
import { stateCoordinates, heightToZoom } from "@/lib/stateCoordinates";
import { getMarkerColor } from "@/lib/markerColors";
import { cityMatches } from "@/lib/mapNav";
import type { Hospital, ScenarioPricing } from "@/types/map";

// Escape user/DB strings before injecting into InfoWindow HTML.
const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

interface UseGoogleMap2DOptions {
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  isAhp: boolean;
  isDark?: boolean;
  activeScenarioId: string | null;
  scenarioHospitalPricing: ScenarioPricing;
  isDataReady: boolean;
  onHospitalClick: (hospital: Hospital) => void;
}

// AHP LIGHT — patriotic cream land + soft navy water, muted roads so the
// red/gold/green markers pop and the whole map reads as part of the app.
const AHP_MAP_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "var(--civic-cream)" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#5b6b85" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "var(--civic-cream)" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f3efe6" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#d6e0f0" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#8a9bbd" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#ece7da" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#c9b896" }, { weight: 0.6 }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#9fb0cb" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
];

// AHP DARK — navy land + deep-navy water to match the app's dark theme.
const AHP_DARK_MAP_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "var(--civic-bg-dark)" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8ba0c0" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "var(--civic-bg-dark)" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "var(--civic-navy-deeper)" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#060f1f" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3f5377" }] },
  { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "var(--civic-navy-deep)" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#6b7f9e" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "var(--civic-navy-deep)" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "var(--civic-navy-deep)" }, { weight: 0.6 }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#3a5a82" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
];

// Dark theme map style for Luigi's
const DARK_MAP_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#212121" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#212121" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#000000" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3d3d3d" }] },
  { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "#2c2c2c" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3c3c3c" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
];

export function useGoogleMap2D({
  containerRef, isAhp, isDark = false, activeScenarioId, scenarioHospitalPricing,
  isDataReady, onHospitalClick,
}: UseGoogleMap2DOptions) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const hospitalsRef = useRef<Hospital[]>([]);
  const initPromiseRef = useRef<Promise<void> | null>(null);

  const initialize = useCallback(async () => {
    if (mapRef.current || !containerRef.current) return;
    if (initPromiseRef.current) return initPromiseRef.current;

    initPromiseRef.current = (async () => {
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
      if (!apiKey) {
        // No key is the INTENDED state since 2026-07-09 (Google ditched; the
        // OSM/Leaflet map is primary) — not an error worth console noise.
        if (import.meta.env.DEV) console.info("Google Maps key not set — OSM map drives (by design)");
        // Fire the shared failure flag so the OSM fallback renders.
        markGoogleMapsFailed();
        return;
      }

      try {
        await loadGoogleMapsScript(apiKey);
        await google.maps.importLibrary("marker");
      } catch (err) {
        console.error("Google Maps failed to load:", err);
        markGoogleMapsFailed();
        initPromiseRef.current = null; // allow a later retry
        throw err; // let callers flip their fallback state too
      }

      if (!containerRef.current || mapRef.current) return;

      const usaCoords = stateCoordinates['USA'];
      const map = new google.maps.Map(containerRef.current, {
        center: { lat: 39.8, lng: -98.5 },
        zoom: 4,
        styles: isAhp ? (isDark ? AHP_DARK_MAP_STYLE : AHP_MAP_STYLE) : DARK_MAP_STYLE,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
        gestureHandling: 'greedy',
        mapId: 'hospital-map-2d',
      });

      mapRef.current = map;
      infoWindowRef.current = new google.maps.InfoWindow();

      google.maps.event.addListenerOnce(map, 'tilesloaded', () => {
        setMapLoaded(true);
        // Offset map so USA is centered in the visible gap between
        // the top search bar (~85px) and the bottom scenario panel (580px tall, anchored at bottom).
        // Without this offset, the map center (39.8°N) is hidden under the scenario panel
        // and the visible gap shows Canada.
        if (containerRef.current) {
          const h = containerRef.current.clientHeight;
          const bottomPanelH = 580;
          const topBarH = 85;
          const visibleBottom = h - bottomPanelH;
          if (visibleBottom > topBarH) {
            const visibleCenter = (topBarH + visibleBottom) / 2;
            const containerCenter = h / 2;
            const offset = Math.round(visibleCenter - containerCenter);
            // offset is negative (visible center is above container center)
            // panBy(0, -offset) with positive value = viewport shifts down = shows more south
            if (Math.abs(offset) > 10) mapRef.current?.panBy(0, -offset);
          }
        }
      });
    })();

    return initPromiseRef.current;
  }, [containerRef, isAhp, isDark]);

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach(m => m.map = null);
    markersRef.current = [];
  }, []);

  const addHospitalMarkers = useCallback((hospitals: Hospital[], maxMarkers = 1000) => {
    if (!mapRef.current) return;
    hospitalsRef.current = hospitals;

    clearMarkers();
    if (infoWindowRef.current) infoWindowRef.current.close();

    const subset = hospitals.slice(0, maxMarkers);

    subset.forEach(hospital => {
      if (!hospital.latitude || !hospital.longitude) return;

      const color = getMarkerColor({
        outlierPercentage: hospital.outlier_percentage || 0,
        dataReady: isDataReady,
        followed: hospital.followed,
        totalProcedures: hospital.total_procedures || 0,
        totalChargemasterFiles: hospital.total_chargemaster_files || 0,
        hospitalId: hospital.id,
        activeScenarioId,
        scenarioHospitalPricing,
      });

      const pinElement = document.createElement('div');
      pinElement.style.width = '14px';
      pinElement.style.height = '14px';
      pinElement.style.borderRadius = '50%';
      pinElement.style.backgroundColor = color;
      pinElement.style.border = `2px solid ${color}`;
      pinElement.style.boxShadow = `0 0 4px ${color}80`;
      pinElement.style.cursor = 'pointer';
      pinElement.style.transition = 'transform 0.15s ease';

      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: mapRef.current!,
        position: { lat: hospital.latitude, lng: hospital.longitude },
        content: pinElement,
        title: hospital.name,
      });

      marker.addListener('click', () => {
        onHospitalClick(hospital);

        if (infoWindowRef.current && mapRef.current) {
          const scenarioPricing = scenarioHospitalPricing.get(hospital.id);
          const priceInfo = activeScenarioId && scenarioPricing
            ? `<div style="font-size:12px;color:#666;">Scenario: $${Math.round(scenarioPricing.blendedPrice).toLocaleString()} (${scenarioPricing.blendedMarkup > 0 ? '+' : ''}${Math.round(scenarioPricing.blendedMarkup)}%)</div>`
            : hospital.outlier_percentage !== undefined
              ? `<div style="font-size:12px;color:#666;">Outlier: ${hospital.outlier_percentage.toFixed(1)}%</div>`
              : '';

          infoWindowRef.current.setContent(`
            <div style="padding:4px;max-width:240px;">
              <div style="font-weight:600;font-size:13px;">${escapeHtml(hospital.name)}</div>
              <div style="font-size:12px;color:#888;">${escapeHtml(hospital.city)}, ${escapeHtml(hospital.state)}</div>
              ${priceInfo}
              <a href="/hospital/${hospital.id}" style="display:inline-block;margin-top:6px;font-size:12px;font-weight:600;color:#2563eb;text-decoration:none;">View prices &rarr;</a>
            </div>
          `);
          infoWindowRef.current.open(mapRef.current, marker);
        }
      });

      pinElement.addEventListener('mouseenter', () => { pinElement.style.transform = 'scale(1.5)'; });
      pinElement.addEventListener('mouseleave', () => { pinElement.style.transform = 'scale(1)'; });

      markersRef.current.push(marker);
    });
  }, [isDataReady, activeScenarioId, scenarioHospitalPricing, clearMarkers, onHospitalClick]);

  const flyToState = useCallback((stateCode: string) => {
    if (!mapRef.current) return;
    const coords = stateCoordinates[stateCode];
    if (!coords) return;

    const zoom = heightToZoom(coords.height);
    mapRef.current.panTo({ lat: coords.latitude, lng: coords.longitude });
    mapRef.current.setZoom(zoom);
  }, []);

  const flyToCity = useCallback((city: string, stateCode: string, hospitals: Hospital[]) => {
    if (!mapRef.current) return;

    const cityHospitals = hospitals.filter(h =>
      cityMatches(h.city, city) &&
      h.state.toUpperCase() === stateCode.toUpperCase() &&
      h.latitude && h.longitude
    );

    if (cityHospitals.length === 0) {
      flyToState(stateCode);
      return;
    }

    const avgLat = cityHospitals.reduce((s, h) => s + h.latitude, 0) / cityHospitals.length;
    const avgLng = cityHospitals.reduce((s, h) => s + h.longitude, 0) / cityHospitals.length;

    mapRef.current.panTo({ lat: avgLat, lng: avgLng });
    mapRef.current.setZoom(11);
  }, [flyToState]);

  const panToHospital = useCallback((hospital: Hospital) => {
    if (!mapRef.current || !hospital.latitude || !hospital.longitude) return;
    mapRef.current.panTo({ lat: hospital.latitude, lng: hospital.longitude });
    mapRef.current.setZoom(14);
    // Offset upward so hospital pin sits in top 35% of map (above bottom panel)
    setTimeout(() => {
      const div = mapRef.current?.getDiv();
      if (div) mapRef.current?.panBy(0, div.clientHeight * 0.2);
    }, 100);
  }, []);

  const flyToLocation = useCallback((lat: number, lng: number) => {
    if (!mapRef.current) return;
    mapRef.current.panTo({ lat, lng });
    mapRef.current.setZoom(11);
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      clearMarkers();
      if (infoWindowRef.current) infoWindowRef.current.close();
      mapRef.current = null;
      initPromiseRef.current = null;
    };
  }, [clearMarkers]);

  return {
    mapRef,
    mapLoaded,
    initialize,
    addHospitalMarkers,
    clearMarkers,
    flyToState,
    flyToCity,
    panToHospital,
    flyToLocation,
  };
}
